import { useRef, useState } from 'react'
import { PanelError } from '@/console/api/errors'
import { ReturnOriginRemapResponse } from '@/console/api/types'
import { requestReturnOriginRemap } from '@/console/data/originRemap'
import { PanelState, loading, ready } from '@/console/state/panelState'
import { useReportBusy } from '@/console/state/useReportBusy'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { Note } from '@/console/ui/Note'
import { ResultState } from '@/console/ui/ResultState'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import {
  DryRunPass,
  EMPTY_ORIGIN_REMAP_FORM,
  ORIGIN_REMAP_RESULT_KIND,
  OriginRemapField,
  OriginRemapFormValues,
  ReturnOriginRemapInput,
  classifyOriginRemapResult,
  deriveOriginRemapGate,
  toOriginRemapError,
} from './originRemap'
import { OriginRemapConfirmDialog } from './OriginRemapConfirmDialog'
import { OriginRemapFormFields } from './OriginRemapFormFields'
import { OriginRemapGuideLink } from './OriginRemapGuideLink'
import { OriginRemapResult } from './OriginRemapResult'
import styles from './OriginRemap.module.css'

type SubmittedResult = { response: ReturnOriginRemapResponse; dryRun: boolean }
type Submission = { values: OriginRemapFormValues; input: ReturnOriginRemapInput; dryRun: boolean }

/**
 * 판정·실행 제출 상태. 비멱등이라 재시도하지 않고, 어떤 호출이든 새로 나가면 이전 판정 통과를 지워
 * 실행이 늘 직전 판정에만 묶이게 한다. `inFlight` 는 리렌더 전에 들어온 두 번째 클릭까지 막는다.
 */
const useOriginRemapSubmission = (onBusyChange?: (busy: boolean) => void) => {
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<SubmittedResult | null>(null)
  const [error, setError] = useState<PanelError | null>(null)
  const [dryRunPass, setDryRunPass] = useState<DryRunPass | null>(null)
  const inFlight = useRef(false)

  useReportBusy(submitting, onBusyChange)

  const submit = ({ values, input, dryRun }: Submission): void => {
    if (inFlight.current) return
    inFlight.current = true
    setSubmitting(true)
    setError(null)
    setResult(null)
    setDryRunPass(null)

    requestReturnOriginRemap({ ...input, dryRun })
      .then((response) => {
        setResult({ response, dryRun })
        if (classifyOriginRemapResult(response, dryRun) === ORIGIN_REMAP_RESULT_KIND.PLANNED) setDryRunPass({ values, changes: response.changes })
      })
      .catch((cause) => setError(toOriginRemapError(cause, dryRun)))
      .finally(() => {
        inFlight.current = false
        setSubmitting(false)
      })
  }

  return { submitting, result, resultState: toResultState(submitting, error, result), dryRunPass, submit }
}

const toResultState = (submitting: boolean, error: PanelError | null, result: SubmittedResult | null): PanelState<SubmittedResult> | null => {
  if (submitting) return loading()
  if (error) return { status: 'error', ...error }
  return result ? ready(result) : null
}

const OriginRemapGuide = (): JSX.Element => (
  <Callout tone="warn">
    A·B·Y 후보를 찾아 주는 조회가 없어요. 입력값은 <OriginRemapGuideLink>가이드 §2.2 조회 SQL</OriginRemapGuideLink>(QueryPie)과 센터 확인으로 구해
    넣으세요. 서버는 A 와 Y 가 <b>같은 합포 송장에 같은 상품인 다른 라인</b>이라는 것까지만 증명해요 — A 가 정말 Y 의 반품인지는 센터 확인이 정해요.
  </Callout>
)

type OriginRemapActionsProps = { canDryRun: boolean; canExecute: boolean; onDryRun: () => void; onExecute: () => void }

const OriginRemapActions = ({ canDryRun, canExecute, onDryRun, onExecute }: OriginRemapActionsProps): JSX.Element => (
  <div className={styles.actions}>
    <Button variant="line" disabled={!canDryRun} onClick={onDryRun}>
      판정(dryRun)
    </Button>
    <Button disabled={!canExecute} onClick={onExecute}>
      실행
    </Button>
  </div>
)

const OriginRemapNoRetryNote = (): JSX.Element => (
  <Note>
    재시도 버튼은 없어요 — 비멱등이라 같은 요청을 다시 보내면 거부되고, 응답을 못 받은 요청은 서버가 반영했는지 알 수 없어요. <b>MOMS_ENG</b> 권한
    필요.
  </Note>
)

type OriginRemapResultAreaProps = { resultState: PanelState<SubmittedResult> | null; result: SubmittedResult | null }

const OriginRemapResultArea = ({ resultState, result }: OriginRemapResultAreaProps): JSX.Element | null => {
  if (!resultState) return null
  return <ResultState state={resultState}>{result ? <OriginRemapResult response={result.response} dryRun={result.dryRun} /> : null}</ResultState>
}

type OriginRemapExecuteConfirmProps = {
  input: ReturnOriginRemapInput | null
  dryRunPass: DryRunPass | null
  onConfirm: () => void
  onCancel: () => void
}

/** 확인창은 판정 통과 계획과 지금 입력이 모두 있을 때만 그린다. */
const OriginRemapExecuteConfirm = ({ input, dryRunPass, onConfirm, onCancel }: OriginRemapExecuteConfirmProps): JSX.Element | null => {
  if (!input || !dryRunPass) return null
  return <OriginRemapConfirmDialog input={input} plannedChanges={dryRunPass.changes} onConfirm={onConfirm} onCancel={onCancel} />
}

const OriginRemapHeading = (): JSX.Element => (
  <SectionHeading
    id="s-inb-origin-remap"
    title="반품 원주문 재매핑"
    badge={<SourceBadge tone="new">신규 · POST /inbound/return-inbound/origin-remap</SourceBadge>}
  />
)

/** 반품 원주문 재매핑(`s-inb-origin-remap`). 판정(dryRun)을 같은 입력으로 통과해야만 실행이 열린다. */
export const OriginRemapPanel = ({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const [values, setValues] = useState<OriginRemapFormValues>(EMPTY_ORIGIN_REMAP_FORM)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { submitting, result, resultState, dryRunPass, submit } = useOriginRemapSubmission(onBusyChange)
  const { input, errors, canDryRun, canExecute, isDryRunStale } = deriveOriginRemapGate(values, dryRunPass, submitting)

  const changeField = (field: OriginRemapField, value: string): void => setValues((previous) => ({ ...previous, [field]: value }))

  const runDryRun = (): void => {
    if (input) submit({ values, input, dryRun: true })
  }

  const execute = (): void => {
    setConfirmOpen(false)
    if (input && canExecute) submit({ values, input, dryRun: false })
  }

  return (
    <section>
      <OriginRemapHeading />
      <Stack>
        <OriginRemapGuide />
        <OriginRemapFormFields values={values} errors={errors ?? {}} disabled={submitting} onChange={changeField} />
        <OriginRemapActions canDryRun={canDryRun} canExecute={canExecute} onDryRun={runDryRun} onExecute={() => setConfirmOpen(true)} />
        {isDryRunStale ? <Callout tone="warn">판정 뒤 입력이 바뀌어 실행을 잠갔어요. 다시 판정하세요.</Callout> : null}
        <OriginRemapResultArea resultState={resultState} result={result} />
      </Stack>
      <OriginRemapNoRetryNote />
      {confirmOpen ? (
        <OriginRemapExecuteConfirm input={input} dryRunPass={dryRunPass} onConfirm={execute} onCancel={() => setConfirmOpen(false)} />
      ) : null}
    </section>
  )
}
